import React, {
  FC,
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import uniqid from "uniqid";
import ReactDOM from "react-dom";
import ReactQuill, {Quill} from "react-quill";
import type Delta from "quill-delta";
import uniq from "lodash/uniq";
import clsx from "clsx";
import {richTextColorPalette} from "constraints/general";
import VariablesIcon from "view/images/VariablesIcon";
import IframeIcon from "view/images/IframeIcon";
import Alert from "view/components/Alert";
import {stripPersistedIframeStatusNotices} from "view/components/RichText/tools/iframeStatusNotices";
import {isExternalRedirectHref} from "view/components/RichText/tools/externalRedirectLink";
import {withImageScale} from "tools/richText/withImageScale";
import {ICustomField} from "@evennt-tech/api-v2-core";
import {CheckVerifiedDomainStatus} from "@evennt-tech/api-v2-external";
import {Typography, useTheme} from "@material-ui/core";
import Tooltip from "@material-ui/core/Tooltip";
import FlexBox from "view/components/Layout/FlexBox";
import SearchInput from "view/components/SearchInput";
import SearchOutlineIcon from "view/images/SearchOutlineIcon";
import {grey} from "@material-ui/core/colors";
import {InfoOutlined} from "@material-ui/icons";
import useStyles, {editorRootHeight, resizeButtonSize} from "./styles";
import {
  ExternalControlType,
  getExternalControl,
} from "./controls/externalControl";
import urlTransformMatcher from "./matchers/urlTransformMatcher";
import {createImageMatcher} from "./matchers/imageMatcher";
import transparentColorButton from "./constants/transparentColorButton";
import {backgroundColorIcon, textColorIcon} from "./constants/colorIcons";
import {
  RICH_TEXT_DEFAULT_VARIABLE_SET_LABEL_BY_TYPE,
  RICH_TEXT_DEFAULT_VARIABLES_BY_NAME,
  RichTextDefaultVariableSetName,
} from "./constants/Variables";
import useOutsideClick from "./hooks/useOutsideClick";
import EditImageModal from "./components/EditImageModal";
import InsertIframeModal from "./components/InsertIframeModal";
import CustomColorControl from "./components/CustomColorControl";
import IframeBlot, {IFRAME_BLOT_NAME, IframeData} from "./blots/IframeBlot";
import {Image, QuillMatcher, RichTextEditorProps} from "./types";
import highlightVariableTokens from "./helpers/highlightVariableTokens";
import isExternalControl from "./helpers/isExternalControl";
import normalizeLinkHref from "./helpers/normalizeLinkHref";
import useImagesEdit, {imageClass} from "./hooks/useImagesEdit";
import {
  FirebaseImageAdapter,
  allowBlobUrls,
  dataUriToBlob,
  getPendingImage,
  registerPendingImage,
  releasePendingImage,
  withSafeConvert,
} from "./adapters";
import {useIframeStatusOverlay} from "./hooks/useIframeStatusOverlay";
import {useIframeHoverToolbar} from "./hooks/useIframeHoverToolbar";

import "react-quill/dist/quill.snow.css";

const ImageQuill = Quill.import("formats/image");
const Parchment = Quill.import("parchment");
Quill.register(
  "modules/clipboard",
  withSafeConvert(Quill.import("modules/clipboard")),
  true
);
Quill.register(IframeBlot, true);
const quillIcons = Quill.import("ui/icons");
quillIcons.color = textColorIcon;
quillIcons.background = backgroundColorIcon;

class ColorAttributor extends Parchment.Attributor.Style {
  add(node: HTMLElement, value: string) {
    if (this.isBlackColor(value)) {
      this.remove(node);
      return true;
    }
    return super.add(node, value);
  }

  // eslint-disable-next-line class-methods-use-this
  private isBlackColor(value: string): boolean {
    if (!value) return true;
    const black = ["black", "rgb(0, 0, 0)", "rgb(0,0,0)", "#000", "#000000"];
    return black.includes(value.toLowerCase().replace(/\s/g, ""));
  }
}

// NOTE: Parchment types are not fully compatible with TS
// eslint-disable-next-line
// @ts-expect-error
const ColorStyle = new ColorAttributor("color", "color", {
  scope: Parchment.Scope.INLINE,
});

// FS-2877 §5: an inline `data-cf-id` attribute binds a custom-field token to a
// specific Show's field id while the visible `‹Title›` text is unchanged. Quill
// emits it on the same span as the token colour; the task resolver reads it to
// resolve per-Show (see tools/task/getTextWithVariables).
const CustomFieldIdAttributor = new Parchment.Attributor.Attribute(
  "cfId",
  "data-cf-id",
  {scope: Parchment.Scope.INLINE}
);

ImageQuill.className = imageClass;
// Quill's default sanitize only allow-lists http/https/data — our deferred
// image flow (paste/drop matcher + EditImageModal save) inserts `blob:`
// object-URLs, which would otherwise be silently rewritten to `//:0` at
// insert time (see adapters/blobImageSanitize.ts for the full bug/fix
// rationale). Every other URL keeps the exact default behavior.
ImageQuill.sanitize = allowBlobUrls(ImageQuill.sanitize.bind(ImageQuill));

Quill.register(ImageQuill, true);
Quill.register(ColorStyle, true);
Quill.register(CustomFieldIdAttributor, true);

const isEmpty = (html: string) => {
  const trimmedHTML = html.trim();
  const regexp = /^<p>(<br>|<br\/>|<br\s\/>|\s+|)<\/p>$/gm;
  return regexp.test(trimmedHTML);
};

const IMG_TAG_PATTERN = /<img\b[^>]*>/gi;
const STYLE_ATTRIBUTE_PATTERN = /\bstyle\s*=\s*(["'])([^"']*)\1/i;

/**
 * Drops the inline `width` `useImagesEdit` paints onto a scaled editor image.
 *
 * That width is presentation only — the authored size lives in the src's
 * `scale` query param, and it is recomputed from there on every render (in the
 * editor, in the read-only views, and in the PDF templates alike).
 *
 * Keeping it out of the persisted HTML matters because Quill's `formats/image`
 * blot only knows `alt` / `height` / `width` — an inline style is a mutation it
 * can't account for. Saved into the value, it also makes what the parent form
 * holds differ from Quill's own model of the document, so a controlled
 * `<ReactQuill value={...}>` rewrites its contents on the next render and drops
 * the caret at the end — typing before an image moved the caret onto it.
 */
const stripImageWidthStyles = (html: string): string =>
  html.replace(IMG_TAG_PATTERN, (tag) =>
    tag.replace(STYLE_ATTRIBUTE_PATTERN, (attribute, quote, value) => {
      const kept = value
        .split(";")
        .filter((declaration: string) => !/^\s*width\s*:/i.test(declaration))
        .map((declaration: string) => declaration.trim())
        .filter(Boolean);
      return kept.length ? `style=${quote}${kept.join("; ")}${quote}` : "";
    })
  );

const sanitizeEditorHtml = (html: string): string =>
  stripImageWidthStyles(
    stripPersistedIframeStatusNotices(html).replace(
      /<div[^>]*class="[^"]*ffair-iframe-toolbar[^"]*"[^>]*>[\s\S]*?<\/div>/g,
      ""
    )
  );

type QuillRange = {index: number; length: number};

// Module-level so the default is one stable reference for the app's
// lifetime. A `matchers = []` default PARAMETER (the previous approach)
// would evaluate to a brand-new array on every render in which the caller
// doesn't pass `matchers` — that array feeds directly into `modules`'s
// useMemo deps below, so it would silently regenerate `modules` (tearing
// down the live Quill instance) on every render, not just when props
// meaningfully change.
const EMPTY_MATCHERS: QuillMatcher[] = [];

// How long after a paste/drop event image mutations stay enabled. Must exceed
// Quill's own deferred paste handling (`setTimeout(..., 1)`) with room to
// spare, while staying short enough that a later unrelated change can't be
// mistaken for part of the insert.
const USER_INSERT_WINDOW_MS = 150;

const RichTextEditor: FC<RichTextEditorProps> = ({
  id,
  onChange,
  defaultValue = "",
  controls = [],
  baseControls,
  matchers = EMPTY_MATCHERS,
  label = "Start typing...",
  maxHeight,
  imagePath,
  externalRedirectShowId,
  toolbarId = "toolbar",
  showCustomFields,
  bindCustomFieldIds,
  customFieldShowNames,
  standCustomFields,
  isLoading = false,
  maxLen,
  getValueRef,
  value: propsValue,
  variableSets,
  additionalVariables,
  imageAdapter,
  disableImages,
}) => {
  const {root, ...classes} = useStyles();
  const theme = useTheme();
  // Several editors can be mounted at once (stacked task drawers, a drawer
  // plus the subtask modal, …). Quill binds its toolbar via a CSS id
  // selector, so a DUPLICATED toolbar id makes the later editor grab the
  // FIRST instance's toolbar node — its buttons/settings then drive the
  // wrong editor. Suffix the id per mount so every instance owns its own
  // toolbar regardless of what the caller passed.
  const [instanceToolbarId] = useState(() => `${toolbarId}-${uniqid()}`);
  const editorRef = useRef<any>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const iframeButtonRef = useRef<HTMLButtonElement | null>(null);
  const lastKnownSelectionRef = useRef<QuillRange | null>(null);
  const pendingIframeSelectionRef = useRef<QuillRange | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dropdownMenuRef = useRef<HTMLDivElement>(null);
  const editTooltipRef = useRef<HTMLDivElement>(null);
  const isResizable = useRef<boolean>(false);
  const highlightedIndexRef = useRef<number>(0);
  const [isOpen, setIsOpen] = useState(false);
  // The variables panel hangs off its button's left edge; on a crowded
  // toolbar that runs past the editor, so it is shifted back inside the root
  // once it has been laid out.
  const [variablesPanelLeft, setVariablesPanelLeft] = useState(0);
  useLayoutEffect(() => {
    if (!isOpen) {
      setVariablesPanelLeft(0);
      return;
    }
    const panel = dropdownMenuRef.current;
    const root = rootRef.current;
    const anchor = panel?.parentElement;
    if (!panel || !root || !anchor) return;
    const rootRect = root.getBoundingClientRect();
    const anchorRect = anchor.getBoundingClientRect();
    // Measured at `left: 0` — the offset is reset on close and the panel
    // remounts when the field list changes shape.
    setVariablesPanelLeft(0);
    const overflow = panel.getBoundingClientRect().right - rootRect.right;
    if (overflow > 0) {
      setVariablesPanelLeft(
        Math.max(-overflow, rootRect.left - anchorRect.left)
      );
    }
  }, [isOpen, showCustomFields, customFieldShowNames]);
  const [image, setImage] = useState<Image | null>(null);
  const [imagesUpdateCounter, setImagesUpdateCounter] = useState<number>(0);
  const [currentInput, setCurrentInput] = useState<string>("");
  const [variableSearch, setVariableSearch] = useState<string>("");
  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const [iframeAnchorEl, setIframeAnchorEl] = useState<HTMLElement | null>(
    null
  );
  const [contentVersion, setContentVersion] = useState(0);

  const adapter = useMemo(
    () => imageAdapter ?? new FirebaseImageAdapter(imagePath),
    [imageAdapter, imagePath]
  );

  const hasVariables = controls.includes("variables");
  const defaultControls = baseControls ?? [
    "bold",
    {list: "bullet"},
    "link",
    {color: richTextColorPalette},
    {background: ["transparent", ...richTextColorPalette]},
  ];
  const mergedControls = uniq([...defaultControls, ...controls]);
  // `disableImages` is a hard ban on file/image work for this form — strip
  // the toolbar control regardless of whether "image" arrived via `controls`
  // or a caller-supplied `baseControls` override; Task 12's paste/matcher
  // scan consumes `allowImages` to reject dropped/pasted images too.
  const allControls = disableImages
    ? mergedControls.filter((control) => control !== "image")
    : mergedControls;
  const allowImages = !disableImages && allControls.includes("image");
  const getQuill = useCallback(() => editorRef.current?.getEditor(), []);
  const customColorFormats = ([
    "color",
    "background",
  ] as const).filter((format) =>
    allControls.some(
      (control) => typeof control === "object" && format in control
    )
  );
  // `modules` (below) is memoized so regenerating it doesn't tear down the
  // live Quill instance on every render. The image matcher still needs to
  // see the LATEST `allowImages` (it can flip via props on an already
  // mounted editor), so route it through a ref read inside a stable wrapper
  // instead of closing over `allowImages` directly in the matchers array.
  const allowImagesRef = useRef(allowImages);
  allowImagesRef.current = allowImages;
  // Open only while a genuine paste/drop is being processed — see the effect
  // that wires the native listeners below for why every image mutation has to
  // be scoped to it.
  const isUserInsertRef = useRef(false);
  // Empty deps: the closure only ever reads refs (never `allowImages`
  // directly), so its identity can stay fixed for the component's lifetime
  // instead of being reallocated every render.
  const imageMatcher = useCallback(
    (node: HTMLImageElement, delta: Delta) =>
      createImageMatcher({
        allowImages: allowImagesRef.current,
        isUserInsert: isUserInsertRef.current,
      })(node, delta),
    []
  );

  useOutsideClick([dropdownMenuRef], () => setIsOpen(false));

  const defaultVariables = useMemo<string[]>(() => {
    const fromSets = (variableSets ?? []).flatMap(
      (setName) => RICH_TEXT_DEFAULT_VARIABLES_BY_NAME[setName]
    );
    return uniq([...fromSets, ...(additionalVariables ?? [])]);
  }, [variableSets, additionalVariables]);

  useImagesEdit({
    imagesUpdateCounter,
    setImage,
    editTooltipElement: editTooltipRef.current,
    rootEditorElement: rootRef.current,
  });

  const iframeOverlayEntries = useIframeStatusOverlay({
    rootEditorElement: rootRef.current,
    contentVersion,
  });

  useIframeHoverToolbar({
    rootEditorElement: rootRef.current,
    editorRef,
    contentVersion,
  });

  useLayoutEffect(() => {
    const isBackgroundControlExisted = allControls.find(
      (control) =>
        typeof control === "object" &&
        Object.keys(control).some((key) => key === "background")
    );
    if (!isBackgroundControlExisted) {
      return;
    }
    const {title, svgIcon, query, style} = transparentColorButton;
    const element = document
      .getElementById(instanceToolbarId)
      ?.querySelector(query);

    if (element) {
      element.innerHTML = `${svgIcon}${title}`;
      element.setAttribute(
        "style",
        style
          .concat(
            `background-color: ${theme.palette.otherElementsBackground.main}`,
            `border: 1px solid ${theme.palette.separator.main}`,
            `color: ${theme.palette.tableRowTitle.main}`
          )
          .join(";")
      );
    }
  }, [allControls]);

  // No dependency array meant this ran after EVERY render (not just mount)
  // with no cleanup — two more permanent "mousemove"/"mouseup" listeners
  // added to `document` on every re-render of this editor, on top of the
  // same bug in useImagesEdit (see that file). `resizeEditor` doesn't
  // depend on any props/state that change across renders (it only reads
  // `rootRef.current` and the event fresh each call), so capturing it once
  // here is safe.
  useLayoutEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      e.stopPropagation();
      if (!isResizable.current) {
        return;
      }
      resizeEditor(e);
    };
    const onMouseUp = () => {
      isResizable.current = false;
      document.body.style.cursor = "auto";
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, []);
  const insertVariable = (variableSymbol: string, cfId?: string) => {
    const quillEditor = editorRef.current?.getEditor();
    const currentRange = quillEditor.getSelection();

    if (currentRange) {
      const contentUpToCaret = quillEditor.getText(0, currentRange.index);
      const lastCharIndex = contentUpToCaret.lastIndexOf("<");

      if (lastCharIndex !== -1) {
        const charactersToDelete = currentRange.index - lastCharIndex;

        quillEditor.deleteText(lastCharIndex, charactersToDelete);

        quillEditor.insertText(lastCharIndex, variableSymbol);
        quillEditor.formatText(
          lastCharIndex,
          variableSymbol.length,
          "color",
          "#2979FF"
        );
        // FS-2877 \u00A75: bind the custom-field token to its Show-specific id.
        if (cfId) {
          quillEditor.formatText(
            lastCharIndex,
            variableSymbol.length,
            "cfId",
            cfId
          );
        }

        quillEditor.insertText(lastCharIndex + variableSymbol.length, "\u00A0");
        quillEditor.formatText(
          lastCharIndex + variableSymbol.length,
          1,
          "color",
          "black"
        );

        quillEditor.setSelection(lastCharIndex + variableSymbol.length + 1);
      }
    }

    // Close the dropdown after inserting the variable
    const dropdown = dropdownRef.current;
    if (dropdown) {
      dropdown.style.display = "none";
    }
  };

  const renderVariableDropdown = () => {
    if (!showCustomFields?.length && !variableSets?.length) {
      return null;
    }

    const allVariables =
      showCustomFields && showCustomFields.length > 0
        ? [...showCustomFields, ...defaultVariables]
        : defaultVariables;

    const normalizeSpace = (str: string) => str.replace(/\u00A0/g, " ");
    const isPrefixMatch = (title: string, currentInput: string) => {
      title = normalizeSpace(title);
      currentInput = normalizeSpace(currentInput);

      const titleWords = title.toLowerCase().split(" ");
      const inputWords = currentInput.toLowerCase().split(" ");

      for (let i = 0; i < inputWords.length; i += 1) {
        if (i >= titleWords.length) return false; // More words in input than title

        if (!titleWords[i].startsWith(inputWords[i])) return false; // Words don't match
      }

      return true;
    };

    const matchedVariables = allVariables
      .filter((variable) => {
        const title = typeof variable === "object" ? variable?.title : variable;
        return isPrefixMatch(title, currentInput);
      })
      .slice(0, 5);

    // Close dropdown if no matches
    const dropdown = dropdownRef.current;
    if (matchedVariables.length === 0 && currentInput) {
      if (dropdown) {
        dropdown.style.display = "none";
      }
      return null;
    }

    return (
      <div
        ref={dropdownRef}
        className="ql-variable-dropdown"
        style={{display: "none", position: "absolute", background: "#FFFFFF"}}
      >
        <div className={classes.variableDropdownMenu}>
          {matchedVariables.map((variable: ICustomField | string, index) => (
            <button
              key={index}
              type="button"
              onClick={() =>
                insertVariable(
                  `<${
                    typeof variable === "object" ? variable?.title : variable
                  }>`,
                  typeof variable === "object" ? variable?.id : undefined
                )
              }
              className={
                highlightedIndexRef.current === index ? "highlighted" : ""
              }
            >
              {typeof variable === "object" ? variable?.title : variable}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const handleVariableClick = (variable: string, cfId?: string) => {
    const quillEditor = editorRef.current.getEditor();
    if (!quillEditor.hasFocus()) {
      quillEditor.focus();
    }

    let range = quillEditor.getSelection();
    if (!range) {
      range = {index: quillEditor.getLength(), length: 0};
    }
    quillEditor.insertText(range.index, variable);
    quillEditor.formatText(range.index, variable.length, "color", "#2979FF");
    // FS-2877 \u00A75: bind the custom-field token to its Show-specific id.
    if (cfId) {
      quillEditor.formatText(range.index, variable.length, "cfId", cfId);
    }

    quillEditor.insertText(range.index + variable.length, "\u00A0");
    quillEditor.formatText(range.index + variable.length, 1, "color", "black");

    // Set the cursor position after the space
    quillEditor.setSelection(range.index + variable.length + 1);
  };

  useLayoutEffect(() => {
    // Scoped to THIS instance's toolbar — a global id lookup would hit the
    // first mounted editor when several are on screen.
    const variableSelect = document
      .getElementById(instanceToolbarId)
      ?.querySelector("#variableSelect");
    if (variableSelect) {
      const options = variableSelect.querySelectorAll(".ql-picker-item");
      options.forEach((option) => {
        const label = option.getAttribute("data-label");
        if (label) {
          option.textContent = label;
        }
      });
    }
    // `instanceToolbarId` is fixed for the component's lifetime — the
    // effect still runs exactly once.
  }, [instanceToolbarId]);

  const openVariableDropdownAtCursor = () => {
    const quillEditor = editorRef.current.getEditor();
    const range = quillEditor.getSelection();
    if (range) {
      const bounds = quillEditor.getBounds(range.index);
      const dropdown = dropdownRef.current;
      if (dropdown) {
        dropdown.style.left = `${bounds.left}px`;
        dropdown.style.top = `${50 + bounds.top + bounds.height}px`;
        dropdown.style.display = "block";
      }
    }
  };

  useEffect(() => {
    const quillEditor = editorRef.current?.getEditor();
    if (getValueRef && quillEditor) {
      // eslint-disable-next-line
      // @ts-ignore
      getValueRef.current = () =>
        sanitizeEditorHtml(quillEditor.root.innerHTML);
    }
  }, [getValueRef, imagesUpdateCounter, propsValue]);

  useEffect(() => {
    if (!hasVariables) {
      return;
    }

    const quillEditor = editorRef?.current?.getEditor();
    const handleTextChange = () => {
      const cursorPosition = quillEditor.getSelection(true).index;
      const content = quillEditor.getText(0, cursorPosition);
      const lastCharIndex = content.lastIndexOf("<");
      if (lastCharIndex !== -1) {
        const textAfterLastChar = content.slice(lastCharIndex + 1);
        setCurrentInput(textAfterLastChar);
        openVariableDropdownAtCursor();
      } else {
        setCurrentInput("");
        const dropdown = dropdownRef.current;
        if (dropdown) {
          dropdown.style.display = "none";
        }
      }
    };

    quillEditor.on("text-change", handleTextChange);

    return () => {
      quillEditor.off("text-change", handleTextChange);
    };
  }, [hasVariables]);

  useEffect(() => {
    if (!hasVariables || isLoading) {
      return;
    }

    const quill = editorRef.current;
    const quillEditor = quill.getEditor();
    const quillContainer = quillEditor.container;

    const handleKeyDown = (e: KeyboardEvent) => {
      const dropdown = dropdownRef.current;
      // Toggle the dropdown with '<'
      if (e.key === "<") {
        if (dropdown && dropdown.style.display === "none") {
          dropdown.style.display = "block";
          highlightedIndexRef.current = 0;
        }
        return;
      }

      if (e.key === ">") {
        if (dropdown && dropdown.style.display === "block") {
          dropdown.style.display = "none";
          setCurrentInput(""); // Clear the input
        }

        let quillContent = quillEditor.getText();
        let cursorPosition = quillEditor.getSelection().index;
        let openingBracketPos = -1;
        for (let i = cursorPosition - 1; i >= 0; i -= 1) {
          if (quillContent[i] === "<") {
            openingBracketPos = i;
            break;
          }
        }

        // Extract and log the text between < and >
        let textBetweenBrackets = "";
        if (openingBracketPos !== -1) {
          textBetweenBrackets = quillContent.substring(
            openingBracketPos + 1,
            cursorPosition
          );

          const customFields =
            showCustomFields?.map((customField) => customField.title) || [];

          const allVariables = [...customFields, ...defaultVariables];
          if (allVariables.includes(textBetweenBrackets)) {
            // Color the text blue, including < and >
            quillEditor.formatText(
              openingBracketPos,
              cursorPosition - openingBracketPos + 1, // Include the `>` character
              "color",
              "#2979FF"
            );
          } else {
            quillEditor.formatText(
              openingBracketPos,
              cursorPosition - openingBracketPos + 1, // Include the `>` character
              "color",
              "black"
            );
          }
        }

        // Using setTimeout to add space and set its color after current event loop
        setTimeout(() => {
          quillEditor.insertText(cursorPosition + 1, "\u00A0");
          quillEditor.formatText(cursorPosition + 1, 1, "color", "black");
          // Set the caret position after the inserted space
          quillEditor.setSelection(cursorPosition + 2);
        });

        return;
      }

      // If dropdown is not open, no further action needed
      if (!dropdown || dropdown.style.display === "none") return;

      const buttons: NodeListOf<HTMLButtonElement> = dropdown.querySelectorAll(
        "button"
      );

      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        dropdown.style.display = "none";
        return;
      }

      if (e.key === "ArrowUp") {
        e.preventDefault();
        e.stopPropagation();
        if (highlightedIndexRef.current > 0) {
          buttons[highlightedIndexRef.current].classList.remove("highlighted");
          highlightedIndexRef.current -= 1;
          buttons[highlightedIndexRef.current].classList.add("highlighted");
        }
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        e.stopPropagation();
        if (highlightedIndexRef.current < buttons.length - 1) {
          buttons[highlightedIndexRef.current].classList.remove("highlighted");
          highlightedIndexRef.current += 1;
          buttons[highlightedIndexRef.current].classList.add("highlighted");
        }
      } else if (e.key === "Enter" || e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
        buttons[highlightedIndexRef.current].click();
        dropdown.style.display = "none";
      }
    };

    quillContainer.addEventListener("keydown", handleKeyDown, true);

    return () => {
      quillContainer.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [showCustomFields, hasVariables, isLoading, defaultVariables]);

  // Stored HTML carries no colour on its variable tokens (seeded templates,
  // text typed before the token list was known), so paint the uncoloured
  // known ones once the editor and the lists are ready. Quill still raises its
  // editor-change for "silent" edits and react-quill forwards that to
  // onChange, so the pass is fenced with a ref that handleChange honours;
  // otherwise the host form would turn dirty on open. Controlled editors are
  // skipped: react-quill re-seeds them whenever `value` differs from its
  // internal state, which would undo the colouring on the next render.
  const isHighlightingVariablesRef = useRef(false);
  const isControlled = propsValue !== undefined;
  useEffect(() => {
    if (!hasVariables || isLoading || isControlled) {
      return;
    }
    const quillEditor = editorRef.current?.getEditor();
    if (!quillEditor) {
      return;
    }
    const known = new Set([
      ...(showCustomFields?.map((customField) => customField.title) ?? []),
      ...defaultVariables,
    ]);
    isHighlightingVariablesRef.current = true;
    try {
      highlightVariableTokens(quillEditor, known);
    } finally {
      isHighlightingVariablesRef.current = false;
    }
  }, [
    hasVariables,
    isLoading,
    isControlled,
    showCustomFields,
    defaultVariables,
  ]);

  /**
   * On mount Quill re-serializes the initial HTML (source "api"): it adds
   * its own classes, rewrites entities and re-wraps blocks, so the emitted
   * string differs from what the caller stored even though nothing was
   * edited. Propagating that event makes host forms dirty the moment they
   * open. Effects run after ReactQuill's componentDidMount (where that
   * synchronous re-serialization fires), so the flag is still `true` for
   * the mount event and `false` for everything a user can trigger.
   */
  const isMountNormalizationRef = useRef(true);
  useEffect(() => {
    isMountNormalizationRef.current = false;
  }, []);

  const inFlightSrcs = useRef(new Set<string>());

  // Opens a short window around a real paste/drop, during which the image
  // matcher and `normalizeDataImages` are allowed to rewrite/strip <img>
  // nodes. Everything outside it — seeding the editor with stored HTML, plain
  // typing, programmatic `setContents` — must leave images exactly as they
  // are: a stored legacy base64 image would otherwise be re-uploaded as a new
  // storage file on every open, or silently deleted in no-image fields.
  //
  // Quill's own paste handling is deferred (`setTimeout(..., 1)`), so the
  // window has to outlive the event itself by more than a tick.
  useEffect(() => {
    const root = editorRef.current?.getEditor().root as HTMLElement | undefined;
    if (!root) {
      return undefined;
    }
    let timer: ReturnType<typeof setTimeout> | null = null;
    const openWindow = () => {
      isUserInsertRef.current = true;
      if (timer) {
        clearTimeout(timer);
      }
      timer = setTimeout(() => {
        isUserInsertRef.current = false;
        timer = null;
      }, USER_INSERT_WINDOW_MS);
    };
    root.addEventListener("paste", openWindow);
    root.addEventListener("drop", openWindow);
    return () => {
      root.removeEventListener("paste", openWindow);
      root.removeEventListener("drop", openWindow);
      if (timer) {
        clearTimeout(timer);
      }
      isUserInsertRef.current = false;
    };
  }, []);

  // Safety net for native file-drop, which bypasses the clipboard matchers
  // in Quill 1.x (only paste goes through `imageMatcher`). Scans the live DOM
  // for any `data:image/` <img> that slipped in and either strips it
  // (no image control / disableImages) or converts it to a registered
  // pending blob — so a raw base64 payload never reaches persisted HTML.
  const normalizeDataImages = useCallback(() => {
    const quill = editorRef.current;
    if (!quill || !isUserInsertRef.current) {
      // Scoped to the paste/drop window for the same reason as the matcher:
      // outside it, a `data:` <img> is stored content, not a fresh insert.
      return;
    }
    const root = quill.getEditor().root as HTMLElement;
    root
      .querySelectorAll<HTMLImageElement>('img[src^="data:image/"]')
      .forEach((img) => {
        if (!allowImages) {
          // covers both "no image control" and the disableImages hard ban
          img.remove();
          return;
        }
        const src = img.getAttribute("src")!;
        const decoded = dataUriToBlob(src);
        if (!decoded) {
          // Un-decodable inline payload (non-base64 data: URI or corrupted
          // base64) — same treatment as !allowImages: it can't be persisted.
          img.remove();
          return;
        }
        img.setAttribute("src", registerPendingImage({...decoded, scale: 100}));
      });
  }, [allowImages]);

  // Pasted/dropped images land as `blob:` <img> tags (registered as pending
  // images by the deferred adapter). When the active adapter is immediate
  // (Firebase), those blobs must still be uploaded — scan for them on every
  // change instead of only on the explicit EditImageModal save path.
  const flushImmediateImages = useCallback(() => {
    const quill = editorRef.current;
    if (!adapter.immediate || !quill) {
      return;
    }
    const root = quill.getEditor().root as HTMLElement;
    root
      .querySelectorAll<HTMLImageElement>('img[src^="blob:"]')
      .forEach((img) => {
        const src = img.getAttribute("src");
        if (!src || inFlightSrcs.current.has(src)) {
          return;
        }
        const pending = getPendingImage(src);
        if (!pending) {
          return;
        }
        inFlightSrcs.current.add(src);
        adapter
          .insert(pending)
          .then((finalSrc) => {
            // A pasted/duplicated image can produce several <img> nodes
            // sharing the same pending blob: src — patch every one of them
            // before releasing, or any copy left behind would still point at
            // the (about to be revoked) blob URL.
            root
              .querySelectorAll<HTMLImageElement>('img[src^="blob:"]')
              .forEach((duplicate) => {
                if (duplicate.getAttribute("src") === src) {
                  duplicate.setAttribute("src", finalSrc);
                }
              });
            releasePendingImage(src);
            setImagesUpdateCounter((counter) => counter + 1);
          })
          .catch((error) => {
            // No other error channel is wired for this background scan —
            // without this, a failed upload fails silently and the <img>
            // is left pointing at a blob: src forever.
            // eslint-disable-next-line no-console
            console.error(
              "RichTextEditor: failed to upload a pasted/dropped image",
              error
            );
          })
          .finally(() => inFlightSrcs.current.delete(src));
      });
  }, [adapter]);

  const handleChange = (content: string, _delta: unknown, source: string) => {
    normalizeDataImages();
    flushImmediateImages();
    if (isHighlightingVariablesRef.current) {
      return;
    }
    if (isMountNormalizationRef.current && source !== "user") {
      return;
    }
    // `content` is the HTML Quill captured at the moment the change fired —
    // BEFORE the DOM mutations above ran. `normalizeDataImages` strips/
    // rewrites `data:image/` srcs in place, so re-read the live root here;
    // otherwise a raw base64 payload from a native file-drop would still
    // reach `onChange` even though the DOM itself was cleaned up.
    const latestHtml = editorRef.current?.getEditor().root.innerHTML ?? content;
    // Strip the editor-only iframe hover toolbar from persisted HTML so it
    // doesn't render on the read-only Show Info / public views. Also drop
    // saved iframe status notices; current status is rendered dynamically.
    const sanitized = sanitizeEditorHtml(latestHtml);
    const value = isEmpty(sanitized) ? "" : sanitized;
    if (maxLen) {
      const len = htmlToInnerText(sanitized).length;
      if (len > maxLen) {
        onChange(propsValue || "");
        return;
      }
    }
    onChange(value);
  };

  const updateLinksInEditor = () => {
    const quillEditor = editorRef.current?.getEditor();

    if (!quillEditor) return;

    const links = quillEditor.root.querySelectorAll("a");

    links.forEach((link: HTMLAnchorElement) => {
      const href = normalizeLinkHref(link.getAttribute("href"));
      if (href) {
        link.setAttribute("href", href);
      }
    });
  };

  useEffect(() => {
    // Guarded because a passive effect can run after the component has already
    // been detached, which leaves this ref null.
    const quillEditor = editorRef.current?.getEditor();

    if (!quillEditor) return undefined;

    const handleTextChange = () => {
      updateLinksInEditor();
      setContentVersion((prev) => prev + 1);
    };

    quillEditor.on("text-change", handleTextChange);

    return () => {
      quillEditor.off("text-change", handleTextChange);
    };
  }, []);

  // Integration links are stored as Quill links, so the toolbar would mark the
  // link control active on them and the snow theme would open its link
  // tooltip (Visit URL / Edit / Remove) over them. Registered after both, so
  // it runs last and has the final say.
  useEffect(() => {
    const quillEditor = editorRef.current?.getEditor();
    const linkButton = document
      .getElementById(instanceToolbarId)
      ?.querySelector(".ql-link");

    if (!quillEditor) return undefined;

    // Reads the selection Quill already holds, like its own toolbar does:
    // `getSelection()` would re-read the DOM mid-change and emit a stale caret
    // move, which switches a pending integration link off while typing.
    const syncLinkButton = (eventName?: unknown, eventRange?: unknown) => {
      const range =
        eventName === "selection-change"
          ? (eventRange as QuillRange | null)
          : quillEditor.selection.getRange()[0];
      const link = range ? quillEditor.getFormat(range).link : undefined;
      if (typeof link === "string" && isExternalRedirectHref(link)) {
        linkButton?.classList.remove("ql-active");
        quillEditor.theme?.tooltip?.hide();
      }
    };

    quillEditor.on("editor-change", syncLinkButton);
    quillEditor.on("scroll-optimize", syncLinkButton);

    return () => {
      quillEditor.off("editor-change", syncLinkButton);
      quillEditor.off("scroll-optimize", syncLinkButton);
    };
  }, [instanceToolbarId]);

  useEffect(() => {
    const quillEditor = editorRef.current?.getEditor();

    if (!quillEditor) return undefined;

    const handleSelectionChange = (range: QuillRange | null) => {
      if (range) {
        lastKnownSelectionRef.current = range;
      }
    };

    quillEditor.on("selection-change", handleSelectionChange);

    return () => {
      quillEditor.off("selection-change", handleSelectionChange);
    };
  }, []);

  // Reads `allowImagesRef.current` (not `allowImages` directly) for the same
  // reason as `imageMatcher` above: this handler is wired into the memoized
  // `modules.toolbar.handlers`, so closing over `allowImages` directly would
  // regenerate `modules` (and tear down Quill) every time `disableImages`
  // toggles on an already-mounted editor.
  const imageHandler = useCallback(() => {
    if (!allowImagesRef.current) {
      return;
    }
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", ".jpg, .jpeg, .png");
    input.click();
    input.onchange = async () => {
      if (input.files) {
        const file = input.files[0];
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(file);
        const reader = new FileReader();
        reader.onload = function () {
          const extension = file.type.split("image/")[1] as Image["extension"];
          setImage({
            dataString: reader.result?.toString() || "",
            extension,
            scale: 100,
          });
        };
        reader.readAsDataURL(file);
      }
    };
  }, []);

  /*
   * No `externalRedirectShowId` guard: the inserted link carries a placeholder
   * rather than a Show id, which is what lets a library master hold one. The
   * prop is only the iframe modal's domain-verification scope now.
   */
  const aspLinkHandler = useCallback(() => {
    const control = getExternalControl(ExternalControlType.asp);
    control.handler(editorRef.current.getEditor());
  }, []);

  const visitkHandler = useCallback(() => {
    const control = getExternalControl(ExternalControlType.visit);
    control.handler(editorRef.current.getEditor());
  }, []);

  const expoPlatformHandler = useCallback(() => {
    const control = getExternalControl(ExternalControlType.expoPlatform);
    control.handler(editorRef.current.getEditor());
  }, []);

  const sitkaHandler = useCallback(() => {
    const control = getExternalControl(ExternalControlType.sitka);
    control.handler(editorRef.current.getEditor());
  }, []);

  const afitusHandler = useCallback(() => {
    const control = getExternalControl(ExternalControlType.aditus);
    control.handler(editorRef.current.getEditor());
  }, []);

  const jetHandler = useCallback(() => {
    const control = getExternalControl(ExternalControlType.jet);
    control.handler(editorRef.current.getEditor());
  }, []);

  const iframeHandler = useCallback(() => {
    const anchor = iframeButtonRef.current;
    const quill = editorRef.current?.getEditor();
    const selection = quill?.getSelection();
    if (selection) {
      lastKnownSelectionRef.current = selection;
    }
    pendingIframeSelectionRef.current =
      selection || lastKnownSelectionRef.current;
    // Defer past the current click so Quill finishes its toolbar focus cycle
    // before the iframe popover takes focus.
    setTimeout(() => {
      setIframeAnchorEl(anchor);
      setIframeModalOpen(true);
    }, 0);
  }, []);

  const closeIframeModal = useCallback(() => {
    setIframeModalOpen(false);
    setIframeAnchorEl(null);
    pendingIframeSelectionRef.current = null;
  }, []);

  const insertIframe = useCallback((data: IframeData) => {
    const quill = editorRef.current?.getEditor();
    if (!quill) return;
    // IMPORTANT: do NOT pass `true` to getSelection — that forces focus on
    // Quill, which steals focus from the still-mounted InsertIframeModal
    // and trips MUI Modal's focus-trap defenses, closing the parent modal.
    const selection = quill.getSelection();
    const range =
      selection ||
      pendingIframeSelectionRef.current ||
      lastKnownSelectionRef.current;
    const index = range ? range.index : quill.getLength();
    quill.insertEmbed(index, IFRAME_BLOT_NAME, data, "silent");
    quill.setSelection(index + 1);
    lastKnownSelectionRef.current = {index: index + 1, length: 0};
    pendingIframeSelectionRef.current = null;
    setContentVersion((prev) => prev + 1);
  }, []);

  // react-quill deep-compares `modules` and rebuilds Quill when a matcher
  // function differs — under OUR toolbar, which stays mounted. The dead
  // instance's click handlers then run first on every button and clear the
  // selection, so formatting silently no-ops. Callers legitimately hand in a
  // fresh closure per render (`termUrlMatcher` closes over the terms list),
  // so Quill gets fixed wrappers that read the latest matcher through a ref;
  // only the selector list is a genuine new module set.
  const matchersRef = useRef(matchers);
  matchersRef.current = matchers;
  const matcherSignature = matchers
    .map(([selector]) => String(selector))
    .join(" ");
  const stableMatchersRef = useRef<{
    signature: string;
    matchers: QuillMatcher[];
  }>();
  if (stableMatchersRef.current?.signature !== matcherSignature) {
    stableMatchersRef.current = {
      signature: matcherSignature,
      matchers: matchers.map(([selector], index) => [
        selector,
        (node: unknown, delta: Delta) => {
          const matcher = matchersRef.current[index]?.[1];
          return typeof matcher === "function" ? matcher(node, delta) : delta;
        },
      ]),
    };
  }
  const stableMatchers = stableMatchersRef.current.matchers;

  const modules = useMemo(
    () => ({
      toolbar: {
        container: `#${instanceToolbarId}`,
        handlers: {
          image: imageHandler,
          aspLink: aspLinkHandler,
          visitLink: visitkHandler,
          expoPlatformLink: expoPlatformHandler,
          sitkaLink: sitkaHandler,
          aditusLink: afitusHandler,
          jetLink: jetHandler,
          iframe: iframeHandler,
        },
      },
      // strip out the unnecessarily-injected <p><br></p>
      clipboard: {
        matchVisual: false,
        matchers: [
          ["IMG", imageMatcher],
          urlTransformMatcher,
          ...stableMatchers,
        ],
      },
    }),
    [
      imageMatcher,
      aspLinkHandler,
      visitkHandler,
      sitkaHandler,
      afitusHandler,
      jetHandler,
      expoPlatformHandler,
      stableMatchers,
      imageHandler,
      iframeHandler,
      instanceToolbarId,
    ]
  );

  const handleVariableInsert = (variable: string, cfId?: string) => {
    handleVariableClick(variable, cfId);
    setIsOpen(false);
  };

  // The flat legacy list stays the default so the shared consumers (glossary,
  // email, …) are untouched.
  const isIdBoundCustomFields = Boolean(
    (bindCustomFieldIds || customFieldShowNames) &&
      showCustomFields &&
      showCustomFields.length > 0
  );

  const matchesVariableSearch = (label: string) => {
    const query = variableSearch.trim().toLowerCase();
    return !query || label.toLowerCase().includes(query);
  };

  // One heading per set, as in the flat list: Tasks pass only `stand`
  // (STAND PROPERTIES), Show Info passes Show details + On-site contact
  // details, which must not land under a stand heading (FS-3110).
  const renderDefaultVariableSets = () =>
    (variableSets ?? []).map((setName) => {
      const stand =
        setName === RichTextDefaultVariableSetName.stand && standCustomFields
          ? standCustomFields
          : [];
      const variables = uniq([
        ...RICH_TEXT_DEFAULT_VARIABLES_BY_NAME[setName],
        ...stand,
      ]).filter(matchesVariableSearch);

      return (
        <Fragment key={setName}>
          <span className={classes.variablesColumnTitle}>
            {RICH_TEXT_DEFAULT_VARIABLE_SET_LABEL_BY_TYPE[
              setName
            ].toUpperCase()}
          </span>
          {variables.map((variable) => (
            <button
              key={variable}
              type="button"
              onClick={() => handleVariableInsert(`<${variable}>`)}
            >
              {variable}
            </button>
          ))}
        </Fragment>
      );
    });

  const renderCustomFieldButton = (field: ICustomField) => (
    <button
      // Inline, to beat Quill's `.ql-snow.ql-toolbar button {width: 28px}`.
      style={{width: "100%"}}
      key={field.id}
      type="button"
      title={field.title}
      onClick={() => handleVariableInsert(`<${field.title}>`, field.id)}
    >
      <span className={classes.variableLabel}>{field.title}</span>
    </button>
  );

  const renderIdBoundCustomFields = () => {
    if (!customFieldShowNames) {
      return (showCustomFields ?? [])
        .filter((field) => matchesVariableSearch(field.title))
        .map(renderCustomFieldButton);
    }

    const groups: Record<string, ICustomField[]> = {};
    (showCustomFields ?? []).forEach((customField) => {
      const key = customField.showId ?? "";
      (groups[key] = groups[key] ?? []).push(customField);
    });

    return Object.entries(groups).map(([showId, fields]) => {
      const visible = fields.filter((field) =>
        matchesVariableSearch(field.title)
      );
      if (!visible.length) {
        return null;
      }
      return (
        <div key={showId} className={classes.variablesShowGroup}>
          <span
            className={clsx(classes.variablesShowName, classes.variableLabel)}
            title={customFieldShowNames?.[showId]}
          >
            {customFieldShowNames?.[showId] ?? ""}
          </span>
          {visible.map(renderCustomFieldButton)}
        </div>
      );
    });
  };

  const renderControl = (control: string | Record<string, unknown>) => {
    if (isExternalControl(control)) {
      const extrnalControl = getExternalControl(
        control as ExternalControlType.asp
      );
      return extrnalControl.render();
    }

    if (typeof control === "string" && control === "variables") {
      return (
        <div className={classes.variableDropdownContainer}>
          <button type="button" onClick={() => setIsOpen(!isOpen)}>
            <VariablesIcon />
          </button>
          {isOpen && isIdBoundCustomFields && (
            <div
              ref={dropdownMenuRef}
              className={classes.variablesPanel}
              style={{left: variablesPanelLeft}}
            >
              <SearchInput
                transparent
                className={classes.variablesSearch}
                icon={
                  <SearchOutlineIcon className={classes.variablesSearchIcon} />
                }
                placeholder="Search"
                defaultValue={variableSearch}
                onChange={setVariableSearch}
              />
              <div className={classes.variablesColumns}>
                <div className={classes.variablesColumn}>
                  <span className={classes.variablesColumnTitle}>
                    CUSTOM FIELDS
                  </span>
                  {renderIdBoundCustomFields()}
                </div>
                <div className={classes.variablesColumn}>
                  {renderDefaultVariableSets()}
                </div>
              </div>
              {customFieldShowNames && (
                <FlexBox gap={0.5} alignCenter pt={1}>
                  <InfoOutlined className={classes.infoIcon} />
                  <Typography variant="h4" style={{color: grey[500]}}>
                    The selected field will only resolve for the Show it came
                    from.
                  </Typography>
                </FlexBox>
              )}
            </div>
          )}
          {isOpen && !isIdBoundCustomFields && (
            <div
              ref={dropdownMenuRef}
              className={classes.variableDropdownMenu}
              style={{left: variablesPanelLeft}}
            >
              {showCustomFields && showCustomFields.length > 0 && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <span
                    style={{
                      width: "100%",
                      color: "#757575",
                      whiteSpace: "nowrap",
                    }}
                  >
                    CUSTOM FIELDS
                  </span>
                  {showCustomFields.map((customField, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() =>
                        handleVariableInsert(`<${customField.title}>`)
                      }
                    >
                      {customField.title}
                    </button>
                  ))}
                </div>
              )}
              {!!variableSets?.length &&
                variableSets.map((setName) => {
                  const label =
                    RICH_TEXT_DEFAULT_VARIABLE_SET_LABEL_BY_TYPE[setName];
                  const defaultVariables =
                    RICH_TEXT_DEFAULT_VARIABLES_BY_NAME[setName];
                  const standCustomFieldVariables =
                    setName === RichTextDefaultVariableSetName.stand &&
                    standCustomFields
                      ? standCustomFields
                      : [];
                  // uniq: call sites may pass a variable (e.g. "Build Type")
                  // that is already in the default set — render it once.
                  const allVariables = uniq([
                    ...defaultVariables,
                    ...standCustomFieldVariables,
                  ]);

                  return (
                    <Fragment key={setName}>
                      <span
                        style={{
                          width: "100%",
                          color: "#757575",
                          whiteSpace: "nowrap",
                          textTransform: "uppercase",
                        }}
                      >
                        {label}
                      </span>
                      {allVariables.map((variable: string, index) => (
                        <button
                          key={index}
                          type="button"
                          onClick={() => handleVariableInsert(`<${variable}>`)}
                        >
                          {variable}
                        </button>
                      ))}
                    </Fragment>
                  );
                })}
            </div>
          )}
        </div>
      );
    }
    if (typeof control === "string" && control === "iframe") {
      return (
        <Tooltip title="IFrame" key="iframe">
          <button
            ref={iframeButtonRef}
            type="button"
            className={`ql-iframe ${classes.iframeToolbarButton}`}
          >
            <IframeIcon height={18} width={18} />
          </button>
        </Tooltip>
      );
    }
    if (typeof control === "string") {
      return <button type="button" className={`ql-${control}`} />;
    }
    const [[controlName, controlValues]] = Object.entries(control);
    if (controlName === "list") {
      return <button type="button" className="ql-list" value="bullet" />;
    }
    return (
      <select className={`ql-${controlName}`}>
        {(controlValues as string[]).map((value) => (
          <option key={value} value={value} />
        ))}
      </select>
    );
  };

  const resizeEditor = (event: MouseEvent) => {
    const rootCurrent = rootRef?.current;
    if (!rootCurrent) {
      return;
    }

    const newHeight =
      event.clientY -
      rootCurrent.getBoundingClientRect().y +
      resizeButtonSize / 2;
    if (newHeight < editorRootHeight) {
      return;
    }
    rootCurrent.style.height = `${newHeight}px`;
  };

  const handleAddImage = async (imageSource: string, scale: number) => {
    const quill = editorRef?.current;
    if (!quill || !image) {
      return;
    }
    // `imageSource` is a `data:` URI only when the pixels actually changed:
    // EditImageModal seeds it with the node's own src and replaces it solely
    // on a crop (or it came from the file picker). A resize-only edit must
    // therefore NOT re-upload the file — it only moves the scale, which for
    // an already-stored image lives in the src's query string.
    let url: string;
    if (imageSource.startsWith("data:")) {
      const decoded = dataUriToBlob(imageSource);
      if (!decoded) {
        // Defensive only: a `data:` source always carries a base64 payload
        // in practice — bail rather than crash if it somehow doesn't.
        // Close the modal (`image` state gates its render) instead of leaving
        // it open with no feedback and a guaranteed-identical retry.
        // eslint-disable-next-line no-console
        console.error(
          "RichTextEditor: failed to decode the picked image data URI"
        );
        setImage(null);
        return;
      }
      const {blob, extension} = decoded;
      url = await adapter.insert({blob, extension, scale});
    } else {
      const pending = getPendingImage(imageSource);
      url = pending
        ? // Still awaiting upload. Re-register the SAME blob under a fresh
          // object URL rather than mutating the registry entry in place: the
          // src has to change for Quill's mutation observer to notice the
          // edit (so the form goes dirty), and no network call is involved.
          registerPendingImage({...pending, scale})
        : withImageScale(imageSource, scale);
    }

    const imageElement = image.element ?? null;

    const range = quill.getEditor().getSelection();
    const index = range?.index || 1;
    if (imageElement) {
      const oldSrc = imageElement.getAttribute("src");
      imageElement.src = url;
      // Keep the node's own width in step with what was just saved, otherwise
      // re-editing kept showing the OLD size — resizing a stored `…?scale=50`
      // image down to 30 swapped the src for a query-less `blob:` one while
      // `width: 50%` stayed behind. Same rule as `useImagesEdit`: the default
      // scale writes no inline style at all, so Quill sees no stray mutation.
      imageElement.style.width = scale && scale !== 100 ? `${scale}%` : "";
      if (oldSrc?.startsWith("blob:")) {
        // A copy/pasted image can leave several <img> nodes sharing the same
        // not-yet-uploaded blob: src. `imageElement` was just repointed to
        // `url` above, so this query only matches OTHER nodes still on the
        // old src — only release (revoke) it once none remain.
        const editorRoot = quill.getEditor().root as HTMLElement;
        const stillReferenced = Array.from(
          editorRoot.querySelectorAll<HTMLImageElement>('img[src^="blob:"]')
        ).some((el) => el.getAttribute("src") === oldSrc);
        if (!stillReferenced) {
          releasePendingImage(oldSrc);
        }
      }
    } else {
      quill.getEditor().insertEmbed(index, "image", url);
    }

    quill.getEditor().setSelection(index + 1);

    setImagesUpdateCounter((prev) => prev + 1);
    setImage(null);
  };

  // NOTE: It breaks without wrapping
  const sanitizedPropsValue = useMemo(
    () =>
      typeof propsValue === "string"
        ? stripPersistedIframeStatusNotices(propsValue)
        : propsValue,
    [propsValue]
  );
  const sanitizedDefaultValue = useMemo(
    () =>
      stripPersistedIframeStatusNotices(
        typeof defaultValue === "string" ? defaultValue : ""
      ),
    [defaultValue]
  );
  const wrappedValue =
    typeof sanitizedPropsValue !== "undefined"
      ? {value: sanitizedPropsValue}
      : {};

  return (
    <>
      <div className={root} ref={rootRef}>
        <div
          className={`ql-toolbar ql-snow ${classes.toolbar}`}
          id={instanceToolbarId}
        >
          {allControls.map((control, index) => (
            <Fragment key={typeof control === "string" ? control : index}>
              {renderControl(control)}
            </Fragment>
          ))}
          {renderVariableDropdown()}
        </div>

        <ReactQuill
          preserveWhitespace
          id={id}
          ref={editorRef}
          {...wrappedValue}
          className={classes.editor}
          placeholder={label}
          defaultValue={sanitizedDefaultValue}
          onChange={handleChange as any}
          modules={modules}
          style={{maxHeight}}
        />
        <div
          role="presentation"
          className={classes.resizeButton}
          onMouseDown={() => {
            isResizable.current = true;
            document.body.style.cursor = "ns-resize";
          }}
        >
          <div className={classes.resizeLineBig} />
          <div className={classes.resizeLineSmall} />
        </div>
        <div ref={editTooltipRef} className={classes.editTooltip}>
          Edit image
        </div>
      </div>
      {customColorFormats.map((format) => (
        <CustomColorControl
          key={format}
          toolbarId={instanceToolbarId}
          format={format}
          getQuill={getQuill}
        />
      ))}
      {image && (
        <EditImageModal
          image={image}
          onClose={() => setImage(null)}
          onAddImage={handleAddImage}
        />
      )}
      {iframeModalOpen && (
        <InsertIframeModal
          open
          anchorEl={iframeAnchorEl}
          onClose={closeIframeModal}
          showId={externalRedirectShowId}
          onInsert={(data) => {
            insertIframe(data);
          }}
        />
      )}
      {iframeOverlayEntries.map((entry, index) =>
        ReactDOM.createPortal(
          <div
            key={`${entry.status}-${index}`}
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 8,
              zIndex: 2,
              pointerEvents: "none",
              // Solid backdrop so the iframe behind is fully obscured
              // regardless of whether the inline visibility:hidden we set on
              // the iframe ends up overridden by Quill blot re-renders.
              backgroundColor: "#ffffff",
            }}
          >
            <Alert
              severity={
                entry.status === CheckVerifiedDomainStatus.REJECTED
                  ? "error"
                  : "warning"
              }
              fullWidth
            >
              {entry.status === CheckVerifiedDomainStatus.REJECTED
                ? "Sorry, the link was rejected. The IFrame is not visible to Exhibitors. Please contact support for more details."
                : "Waiting for Approval. Domain sent to FFAIR Team for verification. The IFrame is not visible to Exhibitors. Please contact support for more details."}
            </Alert>
          </div>,
          entry.wrap
        )
      )}
    </>
  );
};

const divId = "ibv39HW(haf931jfas-12jfasm";
const htmlToInnerText = (html: string): string => {
  let tempDiv = document.getElementById("div");
  if (!tempDiv) {
    tempDiv = document.createElement("div");
    tempDiv.setAttribute("id", divId);
    tempDiv.setAttribute("style", "display:none;");
  }

  tempDiv.innerHTML = html;
  return tempDiv.innerText || "";
};

export default RichTextEditor;
